import { useEffect } from "react"
import type { FC, ReactElement } from "react"

import eq from "lodash-es/eq"
import isEmpty from "lodash-es/isEmpty"
import map from "lodash-es/map"

import { ETypographySize, ETypographyTag, Typography } from "@/3514/components"
import { type IUseTranslation, useTranslation } from "@/hooks"
import type { TEmptyRenderCallback } from "@/shared/types/functions"

import { useCoachingChatContext as useChatContext } from "../context"
import { coachingChatComponentsGuards as guards } from "../guards"
// deep import on purpose: the hooks barrel drags the whole chat runtime in
import {
    type TCoachingChatSeedDrafts as TSeedDrafts,
    useCoachingChatSeedDrafts as useSeedDrafts
} from "../hooks/coaching-chat.seed-drafts.hook"
import {
    useCoachingChatConversationStatusSelector as useConversationStatusSelector,
    useCoachingChatFormSelector as useFormSelector
} from "../store"
import { coachingChatStyles as chatStyles } from "../styles"
import {
    ECoachingChatConversationStatus as EConversationStatus,
    ECoachingChatEntityMode as EEntityMode,
    type TCoachingChatSeedQuestionFormField as TFormField
} from "../types"

import { CoachingChatQuestion as Field } from "./coaching-chat.question.component"

const { withModeQuestions: withGuard } = guards

const {
    form: { seedQuestions: listClasses }
} = chatStyles

type TProps = {
    renderGuideModalOpener: TEmptyRenderCallback
}

const List: FC<TProps> = ({ renderGuideModalOpener }: TProps): ReactElement => {
    const { t }: IUseTranslation = useTranslation()

    const {
        entityId,
        entityMode,
        form: { title: chatTitle, ref },
        insights: { isEnabled: areInsightsEnabled }
    }: ReturnType<typeof useChatContext> = useChatContext()

    const { questions, isSubmitted: areSeedsSubmitted }: ReturnType<typeof useFormSelector> = useFormSelector(entityId)

    const chatStatus: EConversationStatus = useConversationStatusSelector(entityId)

    const seedDrafts: TSeedDrafts = useSeedDrafts(entityId)

    // Seeds locked: editing is over, so no draft may resurface in a disabled field. This list is
    // the single owner of that cleanup — it always wraps the questions, and watching the flag here
    // beats hooking the five `submitForm()` call sites spread across the initial loader and the
    // conversation starter, which would drift apart. Plain useEffect, not useUpdateEffect, so it
    // also covers arriving on a page whose seeds are already locked.
    useEffect((): void => {
        if (areSeedsSubmitted) seedDrafts.dropAll()
    }, [areSeedsSubmitted, seedDrafts])

    return (
        <div className={listClasses.container({ areInsightsEnabled })} ref={ref}>
            {Boolean(
                !eq(entityMode, EEntityMode.Light) &&
                    (!eq(chatStatus, EConversationStatus.NotAvailable) || areInsightsEnabled)
            ) && (
                <div className={listClasses.heading()}>
                    <Typography
                        className={listClasses.title()}
                        tag={ETypographyTag.Heading3}
                        size={ETypographySize.Heading2}
                        text={
                            !isEmpty(chatTitle) && eq(chatStatus, EConversationStatus.NotAvailable)
                                ? chatTitle
                                : t("entities.coachingChat.chat.title")
                        }
                    />

                    {renderGuideModalOpener()}
                </div>
            )}

            <div className={listClasses.questionsWrapper()}>
                {map<TFormField, ReactElement>(
                    questions,
                    ({ id }: TFormField, idx: number): ReactElement => <Field key={id} index={idx} />
                ) ?? null}
            </div>
        </div>
    )
}

const GuardedList: FC = withGuard<TProps>(List)

GuardedList.displayName = "CoachingChatQuestions"

export { GuardedList as CoachingChatQuestions }
